import { useEffect, useState } from 'react'
import type { RedeemApp, RedeemAppStatus } from '@/api/client'
import { Button, Field, FieldGroup, Input, Select, Textarea, errorText, useToast } from '@/ui'
import EndGiveaway from './EndGiveaway'
import { STATUS_HELP, Section, useUpdateApp } from './shared'

/** The fields of an app, then the end-of-promotion controls. The dates, the
 *  offer and the names go into the message people receive; the price, product
 *  id and grant fields are the operator's own record and no message quotes
 *  them. */
export default function AppSettings({ app }: { app: RedeemApp }) {
  const [form, setForm] = useState<RedeemApp>(app)
  const update = useUpdateApp()
  const toast = useToast()
  useEffect(() => setForm(app), [app])

  const str = (k: keyof RedeemApp) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const dirty = JSON.stringify(form) !== JSON.stringify(app)

  return (
    <div className="space-y-6">
      <Section title="앱 정보" hint="이름, 해제되는 것, 만료일, 무료 기간 날짜, 제안 내용은 받는 사람에게 가는 메시지에 들어간다. 상품 id, 앱 가격, 코드가 주는 것, 무료 기간, 갱신 가격은 메시지에 쓰이지 않는다. 운영하는 사람이 참고하려고 적어 두는 칸이다.">
        <FieldGroup columns={2}>
          <Field label="이름">
            <Input value={form.name} onChange={str('name')} />
          </Field>
          <Field label="해제되는 것" hint="예: Wobble Pro">
            <Input value={form.product_name} onChange={str('product_name')} />
          </Field>
          <Field label="상품 id" hint="참고용. 메시지에 안 들어간다">
            <Input value={form.product_id} onChange={str('product_id')} />
          </Field>
          {form.platform === 'web' ? (
            <Field label="사이트 주소" hint="예: https://noveldesk.app">
              <Input value={form.store_url ?? ''} onChange={str('store_url')} />
            </Field>
          ) : (
            <Field label="스토어 id" hint="Apple 앱 id 또는 Play 패키지 이름">
              <Input value={form.store_id} onChange={str('store_id')} />
            </Field>
          )}
          <Field label="앱 가격" hint="참고용. 메시지에 안 들어간다. 예: $0.99">
            <Input value={form.app_price} onChange={str('app_price')} />
          </Field>
          <Field label="코드 만료일" hint="YYYY-MM-DD">
            <Input value={form.expires_on} onChange={str('expires_on')} />
          </Field>
          <Field label="앱 무료 기간" hint="무료가 아니면 비운다. YYYY-MM-DD">
            <Input value={form.free_until} onChange={str('free_until')} />
          </Field>
          <Field label="코드 한 장이 주는 것" hint="참고용. 메시지에 안 들어간다">
            <Select value={form.grant_kind} onChange={str('grant_kind')}>
              <option value="permanent">영구 해제</option>
              <option value="trial">무료 기간 뒤 갱신</option>
            </Select>
          </Field>
          <Field label="무료 기간" hint='참고용, 무료 기간형만. 메시지에 안 들어간다. 예: "1 year"'>
            <Input value={form.grant_period} onChange={str('grant_period')} />
          </Field>
          <Field label="갱신 가격" hint='참고용, 무료 기간형만. 메시지에 안 들어간다. 예: "$24.99/year"'>
            <Input value={form.renewal_price} onChange={str('renewal_price')} />
          </Field>
          {form.platform === 'web' && (
            <Field label="제안 내용" hint='DM에 그대로 인용된다. 예: "20% off the first year of the yearly plan ($48, then $60/year)"' className="sm:col-span-2">
              <Input value={form.offer ?? ''} onChange={str('offer')} />
            </Field>
          )}
          <Field label="상태" hint={STATUS_HELP}>
            <Select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as RedeemAppStatus }))}>
              <option value="active">가동</option>
              <option value="paused">멈춤</option>
              <option value="archived">보관</option>
            </Select>
          </Field>
        </FieldGroup>
        <Field label="메모">
          <Textarea rows={3} value={form.notes} onChange={str('notes')} />
        </Field>
        {update.error && <p className="text-table text-status-failed">{errorText(update.error)}</p>}
        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={update.isPending}
            disabled={!dirty}
            onClick={() => update.mutate({ app, patch: form }, { onSuccess: () => toast.success('저장했다') })}
          >
            저장
          </Button>
          {dirty && (
            <Button variant="ghost" onClick={() => setForm(app)}>
              되돌리기
            </Button>
          )}
        </div>
      </Section>
      <EndGiveaway app={app} />
    </div>
  )
}
